// Any page whose session check could not reach the server (offline, a 5xx, a
// proxy's error page, the api restarting in a deploy). The login form would
// ask someone who is signed in to sign in again, so the page says what
// happened and checks again: soon at first, then every half minute
// (retryDelayMs), at once when the network comes back or the window is shown
// or focused again, and on "Try again". The cookie is still good, so it lands
// back on this same address when the server answers. A 401 on any retry is a
// real sign-out: App renders the login form (which, inside the iPhone app,
// tells the app: useNativeSignedOut). The iPhone app hears {type: "offline"}
// once per showing and may draw its own retry screen instead.

import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/Button";
import { AuthFrame } from "@/features/auth/AuthFrame";
import { useAuth } from "@/stores/auth";

import { postToNative, retryDelayMs } from "./embedded";

export function OfflinePage() {
  const rehydrate = useAuth((s) => s.rehydrate);
  const [attempt, setAttempt] = useState(0);
  const [checking, setChecking] = useState(false);
  const busy = useRef(false);
  const mounted = useRef(true);
  const told = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    document.title = "Can't reach Corvus";
    return () => void (document.title = "Corvus");
  }, []);

  // Once per showing: StrictMode runs the effect twice.
  useEffect(() => {
    if (told.current) return;
    told.current = true;
    postToNative({ type: "offline" });
  }, []);

  const retry = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setChecking(true);
    try {
      // Success or a 401 replaces this screen (App re-renders from the store).
      await rehydrate();
    } finally {
      busy.current = false;
      if (mounted.current) {
        setChecking(false);
        setAttempt((n) => n + 1);
      }
    }
  }, [rehydrate]);

  useEffect(() => {
    const timer = window.setTimeout(() => void retry(), retryDelayMs(attempt));
    return () => window.clearTimeout(timer);
  }, [attempt, retry]);

  useEffect(() => {
    const now = () => void retry();
    const onVisible = () => {
      if (document.visibilityState === "visible") now();
    };
    window.addEventListener("online", now);
    window.addEventListener("focus", now);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", now);
      window.removeEventListener("focus", now);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [retry]);

  return (
    <AuthFrame title="Can't reach Corvus">
      <div role="status" aria-live="polite">
        <p className="mt-2 text-base text-fg-muted">
          {checking ? "Trying again…" : "This page will try again by itself."}
        </p>
        <Button className="mt-6 w-full" size="lg" onClick={() => void retry()} loading={checking}>
          Try again
        </Button>
      </div>
    </AuthFrame>
  );
}
